import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { Deal, Proposta, PropostaItem, ComboVariation } from '@/types'
import { normalizePropostaResponse } from '@/utils/proposalApi'
import { calculateDealTotal } from '@/utils/dealCalculations'
import { mapApiPlanToCatalog, filterPlansByCatalogScope, buildProposalItemFromCatalog, isGalaxyPlanLabel, type CatalogPlan, type PlanCatalogScope } from '@/utils/planCatalog'
import { calculateComboValues } from './comboPricing'
import { buildProposalTotalsBreakdown } from './proposalTotalsBreakdown'
import type { ComboData, ComboValue } from './types'
import { buildProposalPayload, getExistingDadosRaw } from './proposalTabPayload'
import { createProposalExportHandlers } from './proposalTabExports'
import { useAuth } from '@/hooks/useAuth'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { useProposalTabProposals } from './useProposalTabProposals'
import { useProposalTabFormActions } from './useProposalTabFormActions'
import { useProposalTabStudio } from './useProposalTabStudio'
import { parseComboVariationsForEdit, parseCombosForEdit, defaultValidadeDate, validadeDateFromProposal, validadeDaysFromDate } from './proposalFormState'
import { findRenegotiationDraft, isRenegotiationProposalStage } from './proposalRenegotiation'
import { generateId } from './types'

function buildGalaxyProposalItem(master: CatalogPlan): PropostaItem {
  const item = buildProposalItemFromCatalog(master)
  const duration = 12
  const installment = Number(item.pricing?.installmentValue || item.valorUnitario || master.valor || 0)
  return {
    ...item,
    id: generateId(),
    quantidade: 1,
    duration,
    catalogPlanId: master.id,
    valorUnitario: installment,
    total: installment * duration,
    pricing: {
      total: installment * duration,
      entry: 0,
      installments: duration,
      installmentValue: installment,
      recorrente: true,
      duracao: duration,
      supportPricingMode: 'FIXO_2000',
    },
  }
}

export function useDealProposalTab(
  deal: Deal,
  onDealPatched: (updates: Partial<Deal>) => void,
  options?: { catalogScope?: PlanCatalogScope },
) {
  const catalogScope = options?.catalogScope ?? 'deals'
  const [saving, setSaving] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [generatingLink, setGeneratingLink] = useState(false)
  const [itens, setItens] = useState<PropostaItem[]>([])
  const [validadeAte, setValidadeAte] = useState(defaultValidadeDate)
  const [observacoes, setObservacoes] = useState('')
  const [desconto, setDesconto] = useState(0)
  const [combos, setCombos] = useState<ComboData[]>([])
  const [comboVariations, setComboVariations] = useState<ComboVariation[]>([])
  const [studioPlan, setStudioPlan] = useState<PropostaItem | null>(null)
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null)
  const [studioOpen, setStudioOpen] = useState(false)
  const [catalogPlans, setCatalogPlans] = useState<CatalogPlan[]>([])

  const {
    proposals,
    setProposals,
    loadingProposals,
    preparingRenegotiationDraft,
    loadProposals,
    ensureRenegotiationDraft,
    handleDeleteProposal: deleteProposal,
    handleCopyLink,
    handleGerarLink: gerarLink,
  } = useProposalTabProposals(deal, onDealPatched)

  useEffect(() => {
    let cancelled = false
    api
      .get('/plans')
      .then(({ data }) => {
        if (cancelled) return
        const list = filterPlansByCatalogScope(
          (data.data ?? data ?? []).map((p: Record<string, unknown>) => mapApiPlanToCatalog(p)),
          catalogScope,
        )
        setCatalogPlans(list as CatalogPlan[])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [catalogScope])

  const draftAutoOpenedRef = useRef<string | null>(null)
  const galaxyBootRef = useRef<string | null>(null)
  const galaxyDealIdRef = useRef(deal.id)
  if (galaxyDealIdRef.current !== deal.id) {
    galaxyDealIdRef.current = deal.id
    galaxyBootRef.current = null
  }

  const applyProposalToForm = useCallback((proposta: Proposta) => {
    setEditingId(proposta.id)
    setItens((proposta.itens ?? proposta.items ?? []).map((i) => ({ ...i })))
    setValidadeAte(validadeDateFromProposal(proposta))
    setObservacoes(proposta.observacoes ?? '')
    setDesconto(proposta.desconto)
    setComboVariations(parseComboVariationsForEdit(proposta))
    setCombos(parseCombosForEdit(proposta))
    setShowForm(true)
  }, [])

  useEffect(() => {
    draftAutoOpenedRef.current = null
  }, [deal.id])

  useEffect(() => {
    const autoOpenDraft = isRenegotiationProposalStage(deal.etapa) || deal.etapa === 'proposta_elaboracao'
    if (!autoOpenDraft || showForm || loadingProposals || preparingRenegotiationDraft) return
    if (draftAutoOpenedRef.current === deal.id) return

    const draft = findRenegotiationDraft(proposals)
    if (!draft) return

    applyProposalToForm(draft)
    draftAutoOpenedRef.current = deal.id
  }, [
    deal.id,
    deal.etapa,
    proposals,
    loadingProposals,
    preparingRenegotiationDraft,
    showForm,
    applyProposalToForm,
  ])

  const { hasRole } = useAuth()
  const stageAllowsEdit =
    deal.etapa === 'proposta_elaboracao' ||
    deal.etapa === 'renegociacao' ||
    deal.etapa === 'ajustar_proposta'
  const canSaveProposal = stageAllowsEdit && hasRole(['closer', 'admin', 'manager', 'bdr'])
  const canEdit = canSaveProposal

  const comboValues: ComboValue[] = useMemo(
    () => calculateComboValues(combos, itens),
    [combos, itens],
  )

  const totalsSummary = useMemo(
    () => buildProposalTotalsBreakdown(itens, comboValues, comboVariations, desconto),
    [itens, comboValues, comboVariations, desconto],
  )

  const calculation = useMemo(() => calculateDealTotal(itens, desconto), [itens, desconto])

  const validadeDias = useMemo(() => validadeDaysFromDate(validadeAte), [validadeAte])

  const resetForm = useCallback(() => {
    setItens([])
    setValidadeAte(defaultValidadeDate())
    setObservacoes('')
    setDesconto(0)
    setEditingId(null)
    setCombos([])
    setComboVariations([])
  }, [])

  const buildPayload = useCallback(
    () =>
      buildProposalPayload({
        dealId: deal.id,
        itens,
        comboValues,
        comboVariations,
        validadeDias,
        observacoes,
        desconto,
        subtotal: totalsSummary.subtotal,
        totalFinal: totalsSummary.displayTotal,
        existingDadosRaw: getExistingDadosRaw(proposals, editingId),
      }),
    [
      deal.id,
      itens,
      comboValues,
      comboVariations,
      validadeDias,
      observacoes,
      desconto,
      totalsSummary,
      proposals,
      editingId,
    ],
  )

  const patchDealValorFromProposal = useCallback(
    (saved: Proposta) => {
      const valor = Number(saved.totalFinal ?? totalsSummary.displayTotal)
      if (valor <= 0) return
      onDealPatched({
        valor,
        valorMensal: calculation.valorMensal,
        valorSetup: calculation.valorSetup,
        desconto,
      })
    },
    [calculation, desconto, onDealPatched, totalsSummary.displayTotal],
  )

  const persistProposal = useCallback(
    async (
      status: string,
      options?: { itensOverride?: PropostaItem[]; keepFormOpen?: boolean; silent?: boolean },
    ) => {
      const itemsToSave = options?.itensOverride ?? itens
      if (itemsToSave.length === 0) {
        if (!options?.silent) toast.error('Adicione pelo menos um item à proposta')
        return false
      }
      if (!canSaveProposal) {
        if (!options?.silent) {
          toast.error('Seu perfil não pode salvar propostas nesta etapa do deal.')
        }
        return false
      }

      const localComboValues = calculateComboValues(combos, itemsToSave)
      const localTotals = buildProposalTotalsBreakdown(
        itemsToSave,
        localComboValues,
        comboVariations,
        desconto,
      )
      const payload = {
        ...buildProposalPayload({
          dealId: deal.id,
          itens: itemsToSave,
          comboValues: localComboValues,
          comboVariations,
          validadeDias,
          observacoes,
          desconto,
          subtotal: localTotals.subtotal,
          totalFinal: localTotals.displayTotal,
          existingDadosRaw: getExistingDadosRaw(proposals, editingId),
        }),
        status,
      }

      if (editingId) {
        const { data } = await api.patch(`/propostas/${editingId}`, payload)
        const saved = normalizePropostaResponse(data)
        if (saved) patchDealValorFromProposal(saved)
        if (!options?.silent) {
          toast.success(
            status === 'rascunho' ? 'Rascunho atualizado!' : 'Proposta atualizada e enviada!',
          )
        }
      } else {
        const { data } = await api.post('/propostas', payload)
        const saved = normalizePropostaResponse(data)
        if (saved) {
          patchDealValorFromProposal(saved)
          if (saved.id) setEditingId(saved.id)
        }
        if (!options?.silent) {
          toast.success(status === 'rascunho' ? 'Rascunho salvo!' : 'Proposta enviada!')
        }
      }

      await loadProposals()
      if (!options?.keepFormOpen) {
        resetForm()
        setShowForm(false)
      }
      return true
    },
    [
      itens,
      canSaveProposal,
      combos,
      comboVariations,
      desconto,
      deal.id,
      validadeDias,
      observacoes,
      proposals,
      editingId,
      patchDealValorFromProposal,
      loadProposals,
      resetForm,
    ],
  )

  const saveProposal = useCallback(
    async (status: string) => {
      setSaving(true)
      try {
        await persistProposal(status)
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao salvar proposta'))
      } finally {
        setSaving(false)
      }
    },
    [persistProposal],
  )

  const handlePlanItemUpdated = useCallback(
    async (updatedItem: PropostaItem, itemIndex: number) => {
      let mergedItems: PropostaItem[] = []
      setItens((prev) => {
        mergedItems = prev.map((item, idx) => (idx === itemIndex ? updatedItem : item))
        return mergedItems
      })
      setEditingItemIndex(null)

      if (!canSaveProposal) return true

      setSaving(true)
      try {
        const saved = await persistProposal('rascunho', {
          itensOverride: mergedItems,
          keepFormOpen: true,
          silent: true,
        })
        if (saved) toast.success('Plano atualizado e salvo na proposta')
        return saved
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao salvar alterações do plano'))
        return false
      } finally {
        setSaving(false)
      }
    },
    [canSaveProposal, persistProposal],
  )

  const flushProposalSave = useCallback(
    async (status = 'rascunho') => {
      if (itens.length === 0) return true
      if (!canSaveProposal) {
        toast.error('Seu perfil não pode salvar propostas nesta etapa do deal.')
        return false
      }
      setSaving(true)
      try {
        return await persistProposal(status, { keepFormOpen: true })
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao salvar proposta'))
        return false
      } finally {
        setSaving(false)
      }
    },
    [itens.length, canSaveProposal, persistProposal],
  )

  const handlePlanItemsAdded = useCallback(
    async (newItems: PropostaItem[], planName: string) => {
      if (newItems.length === 0) return

      let mergedItems: PropostaItem[] = []
      setItens((prev) => {
        mergedItems = [...prev, ...newItems]
        return mergedItems
      })
      setShowForm(true)

      setSaving(true)
      try {
        const saved = await persistProposal('rascunho', {
          itensOverride: mergedItems,
          keepFormOpen: true,
          silent: true,
        })
        if (saved) toast.success(`Plano "${planName}" adicionado e rascunho salvo`)
        return saved
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao salvar rascunho'))
        return false
      } finally {
        setSaving(false)
      }
    },
    [persistProposal],
  )

  useEffect(() => {
    if (catalogScope !== 'rocket' || deal.etapa !== 'proposta_elaboracao' || !canEdit) return
    if (loadingProposals || catalogPlans.length === 0) return
    if (galaxyBootRef.current === deal.id) return

    const draft = findRenegotiationDraft(proposals)
    if (draft && (!showForm || editingId !== draft.id)) return

    const galaxy = catalogPlans.find((plan) => isGalaxyPlanLabel(plan.nome))
    if (!galaxy) return

    galaxyBootRef.current = deal.id
    if (itens.length > 0) return

    void handlePlanItemsAdded([buildGalaxyProposalItem(galaxy)], galaxy.nome).finally(() => {
      setEditingItemIndex(0)
    })
  }, [
    catalogScope,
    deal.etapa,
    deal.id,
    canEdit,
    loadingProposals,
    catalogPlans,
    proposals,
    showForm,
    editingId,
    itens,
    handlePlanItemsAdded,
  ])

  const handleDeleteProposal = useCallback(
    async (id: string) => {
      await deleteProposal(id, () => {
        if (editingId === id) {
          resetForm()
          setShowForm(false)
        }
      })
    },
    [deleteProposal, editingId, resetForm],
  )

  const handleGerarLink = useCallback(async () => {
    await gerarLink({
      itensCount: itens.length,
      editingId,
      buildPayload,
      onSuccess: () => {
        resetForm()
        setShowForm(false)
      },
      setGeneratingLink,
    })
  }, [gerarLink, itens.length, editingId, buildPayload, resetForm])

  const formActions = useProposalTabFormActions({
    setItens,
    setCombos,
    setComboVariations,
    setEditingId,
    setValidadeAte,
    setObservacoes,
    setDesconto,
    setShowForm,
  })

  const exportCtx = useMemo(
    () => ({
      deal,
      proposals,
      editingId,
      draftItens: itens,
      catalogPlans,
    }),
    [deal, proposals, editingId, itens, catalogPlans],
  )

  const {
    exportPPTX: handleExportPPTX,
    exportPDF: handleExportPDF,
    exportVariationsPPTX: handleExportVariationsPPTX,
    exportVariationsPDF: handleExportVariationsPDF,
  } = useMemo(() => createProposalExportHandlers(exportCtx), [exportCtx])

  const { targetProposalForStudio, saveStudioPresentation } = useProposalTabStudio({
    proposals,
    studioPlan,
    setStudioPlan,
    setProposals,
    onDealPatched,
  })

  return {
    proposals,
    loadingProposals,
    preparingRenegotiationDraft,
    saving,
    showForm,
    setShowForm,
    editingId,
    generatingLink,
    itens,
    setItens,
    validadeAte,
    observacoes,
    desconto,
    combos,
    comboValues,
    comboVariations,
    setComboVariations,
    studioPlan,
    setStudioPlan,
    editingItemIndex,
    setEditingItemIndex,
    studioOpen,
    setStudioOpen,
    canEdit,
    totalsSummary,
    resetForm,
    saveProposal,
    flushProposalSave,
    handlePlanItemUpdated,
    handlePlanItemsAdded,
    handleDeleteProposal,
    handleCopyLink,
    handleGerarLink,
    ensureRenegotiationDraft,
    handleExportPPTX,
    handleExportPDF,
    handleExportVariationsPPTX,
    handleExportVariationsPDF,
    ...formActions,
    targetProposalForStudio,
    saveStudioPresentation,
    setValidadeAte,
    setObservacoes,
    setDesconto,
    catalogPlans,
  }
}
